/**
 * Accessibility Utilities
 *
 * - announce(message): write to a shared aria-live="polite" region
 *   so screen readers hear progress updates (Runner uses this).
 * - focusPrimaryHeading(): move keyboard focus to the main <h1> after
 *   a screen transition, for screen-reader + keyboard users.
 */

const ANNOUNCER_ID = 'sr-announcer';

function getAnnouncer() {
  let el = document.getElementById(ANNOUNCER_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = ANNOUNCER_ID;
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.className = 'sr-announcer';
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Announce a message to screen readers via aria-live="polite".
 *
 * Implementation note: we set textContent directly (no clear-then-set
 * dance). Screen readers fire on any textContent change, so this is
 * sufficient for distinct messages. If the Runner ever announces the
 * same string twice in a row and a specific reader swallows it,
 * callers can append a zero-width space or disambiguate the message.
 * Batch 3's Runner accessibility audit (Task 11) will validate this on
 * iOS VoiceOver.
 *
 * @param {string} message
 */
export function announce(message) {
  const el = getAnnouncer();
  el.textContent = message;
}

/**
 * Focus the primary <h1> in the app. Call after navigating to a new screen.
 */
export function focusPrimaryHeading() {
  const app = document.getElementById('app') || document.body;
  const h1 = app.querySelector('h1');
  if (!h1) return;
  if (!h1.hasAttribute('tabindex')) {
    h1.setAttribute('tabindex', '-1');
  }
  h1.focus();
}
